import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { PopoverListItem } from '../interfaces/popover-list-item.interface.js';
import type { ItemClickedDetail } from '../interfaces/event-detail.types.js';
import { ITEM_CLICKED_EVENT, SELECTION_CHANGED_EVENT } from '../interfaces/event-detail.types.js';

/**
 * Reactive controller for multi-item selection.
 * Listens for item-clicked events on the host, toggles item selection,
 * and dispatches selection-changed events. Does NOT close the popover.
 */
export class MultiSelectionController implements ReactiveController {

    private _host: ReactiveControllerHost & EventTarget;
    private _selectedIds = new Set<string>();
    private _itemMap = new Map<string, PopoverListItem>();

    constructor(host: ReactiveControllerHost & EventTarget, popoverElement: HTMLElement) {
        void popoverElement;
        this._host = host;
        host.addController(this);

        this._handleItemClicked = this._handleItemClicked.bind(this);
        (host as EventTarget).addEventListener(ITEM_CLICKED_EVENT, this._handleItemClicked as EventListener);
    }

    /** Returns all currently selected items as full PopoverListItem objects. */
    get selectedItems(): PopoverListItem[] {
        return [...this._selectedIds]
            .map(id => this._itemMap.get(id))
            .filter((item): item is PopoverListItem => item !== undefined);
    }

    /** Clears all selected items. */
    clearSelection(): void {
        this._selectedIds.clear();
        this._itemMap.clear();
    }

    /**
     * Sets the selected items by IDs. Replaces any previous selection.
     * Only IDs that match an item in the provided list are selected.
     */
    setSelectedIds(ids: string[], items: PopoverListItem[]): void {
        this._selectedIds.clear();
        this._itemMap.clear();

        const itemById = new Map(items.map(item => [item.id, item]));
        for (const id of ids) {
            const item = itemById.get(id);
            if (item) {
                this._selectedIds.add(id);
                this._itemMap.set(id, item);
            }
        }

        this._host.requestUpdate();
    }

    /** Required by ReactiveController interface. No-op — setup done in constructor. */
    hostConnected(): void {
        // Listener already attached in constructor
    }

    /**
     * Handles item-clicked events: toggles item in selection set
     * and dispatches selection-changed on the host.
     */
    private _handleItemClicked(event: CustomEvent<ItemClickedDetail>): void {
        const { item } = event.detail;

        if (this._selectedIds.has(item.id)) {
            this._selectedIds.delete(item.id);
            this._itemMap.delete(item.id);
        } else {
            this._selectedIds.add(item.id);
            this._itemMap.set(item.id, item);
        }

        this._host.dispatchEvent(new CustomEvent(SELECTION_CHANGED_EVENT, {
            bubbles: true,
            composed: true,
            detail: { selectedItems: this.selectedItems },
        }));
    }
}
